import { prepareAuthHeaders, resolveApiUrl } from "../api/apiBase";
import { getApiErrorStatus } from "../api/errorUtils";

/**
 * Shipping label helpers for the Orders drawer.
 *
 * The PDF is fetched with the staff member's own session (never a public link)
 * and handed to the browser as a local blob. Printing always goes through the
 * API, which owns the printer address.
 */

export const shippingLabelUrl = (orderId, disposition = "inline") =>
  resolveApiUrl(
    `/orders/${encodeURIComponent(orderId)}/shipping-label?disposition=${
      disposition === "attachment" ? "attachment" : "inline"
    }`,
  );

export function parseContentDispositionFilename(header, fallback = "Etiketa.pdf") {
  const value = String(header || "");
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim());
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = value.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1].trim() : fallback;
}

export async function fetchShippingLabelPdf(orderId, { disposition = "inline", fetchImpl } = {}) {
  const doFetch = fetchImpl || ((...args) => fetch(...args));
  const headers = prepareAuthHeaders(new Headers());
  const response = await doFetch(shippingLabelUrl(orderId, disposition), { headers, cache: "no-store" });

  if (!response.ok) {
    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    const error = new Error("Shipping label request failed");
    error.status = response.status;
    error.data = data;
    throw error;
  }

  const blob = await response.blob();
  return {
    blob,
    filename: parseContentDispositionFilename(response.headers.get("content-disposition")),
  };
}

export const shippingLabelErrorCode = (error) => String(error?.data?.details?.code || "");

/** Why the label buttons are hidden, as a translation key (null when available). */
export function resolveShippingLabelHintKey(summary) {
  if (!summary || summary.available) return null;
  switch (summary.unavailableReason) {
    case "NO_POD":
      return "shippingLabel.missingPod";
    case "SHIPMENT_NOT_ACTIVE":
      return "shippingLabel.shipmentNotActive";
    case "ORDER_CANCELLED":
      return "shippingLabel.orderCancelled";
    default:
      return "shippingLabel.unavailable";
  }
}

/** "Dërguar te printeri" is provider acceptance - the UI never claims paper was printed. */
export function resolveLastPrintKey(lastPrint) {
  switch (lastPrint?.status) {
    case "sent":
      return "shippingLabel.lastSent";
    case "sending":
      return "shippingLabel.lastSending";
    case "failed":
      return "shippingLabel.lastFailed";
    case "unknown":
      return "shippingLabel.lastUnknown";
    default:
      return null;
  }
}

const PRINT_ERROR_KEYS = {
  LABEL_PRINT_IN_PROGRESS: "shippingLabel.errorInProgress",
  LABEL_PRINT_OUTCOME_UNKNOWN: "shippingLabel.errorUnknown",
  LABEL_PRINT_SEND_FAILED: "shippingLabel.errorSendFailed",
  LABEL_PRINTER_NOT_CONFIGURED: "shippingLabel.errorPrinterNotConfigured",
  LABEL_MAIL_NOT_CONFIGURED: "shippingLabel.errorPrinterNotConfigured",
  SHIPPING_LABEL_NO_POD: "shippingLabel.missingPod",
  SHIPPING_LABEL_SHIPMENT_NOT_ACTIVE: "shippingLabel.shipmentNotActive",
  SHIPPING_LABEL_ORDER_CANCELLED: "shippingLabel.orderCancelled",
};

export function describeShippingLabelError(error, t) {
  const key = PRINT_ERROR_KEYS[shippingLabelErrorCode(error)];
  if (key) return t(key);
  if (error?.status === "FETCH_ERROR" || error?.status === "TIMEOUT_ERROR") return t("ordersErrors.network");
  const status = getApiErrorStatus(error);
  if (status === 401) return t("ordersErrors.session");
  if (status === 403) return t("ordersErrors.permission");
  if (status === 404) return t("shippingLabel.errorNotFound");
  return t("shippingLabel.errorGeneric");
}

export const needsRepeatConfirmation = (error) =>
  shippingLabelErrorCode(error) === "LABEL_PRINT_CONFIRM_REPEAT";
